import { useEffect, useState, useRef } from "react";
import { Search } from "lucide-react";
import { useMemo } from "react";
import { Admin_GetMessageList } from "../../../../constants/adminData";
import type { MessageConversation } from "../../../../types/admin";
import { PhotoImage } from "../../../../components/common/PhotoImage";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { apiVariables } from "../../../../services/api/apiVariables";
import { useNavigate } from "react-router-dom";
import { getSensitiveData } from '../../../../utils/secureStorage';
import { useMenuLabels } from '../../../../hooks/useMenuLabels';
import { useTheme } from '../../../../context/ThemeContext';

interface MessagesProps {
  onOpenChat: (receiverId: string, userName: string, photoId?: string) => void;
}

interface Conversation {
  conversationId: number;
  receiverId: string;        
  name: string;
  specialty?: string;
  lastMessage: string;
  time: string;
  unread: number;
  isOnline: boolean;
  profileImage?: string;
  photoId?: string;
}

export default function Messages({ onOpenChat }: MessagesProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);
  const isConnectingRef = useRef(false); 
  const [currentUserId, setCurrentUserId] = useState("");
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const navigate = useNavigate();
  const labels = useMenuLabels();
  const { theme } = useTheme();
  
  useEffect(() => {
    const loadUserId = async () => {
      const uid = (await getSensitiveData("userId")) || "";
      setCurrentUserId((current) => (current === uid ? current : uid));
    };
    loadUserId();
  }, []);
  
  useEffect(() => {
    let ignore = false;

    const loadMessages = async () => {
      try {
        setLoading(true);
        const authToken = (await getSensitiveData("authToken")) || "";
        if (!authToken) {
          if (!ignore) {
            setConversations((current) => (current.length === 0 ? current : []));
          }
          return;
        }
        
        const response = await Admin_GetMessageList(authToken);
        
        const mapped: Conversation[] = response.data.map(
          (item: MessageConversation) => ({
            conversationId: item.conversationId,
            receiverId: item.otherUserId,          
            name: item.otherUserName,
            lastMessage: item.lastMessage,
            time: formatTime(item.lastMessageAt),
            unread: item.unreadCount,
            isOnline: item.isOnline,
            profileImage: item.profileImageUrl,
            photoId: item.photoId,
          })
        );

        if (!ignore) {
          setConversations(mapped);
        }
      } catch (error) {
        if (!ignore) setConversations([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadMessages();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    if (
      isConnectingRef.current ||
      (connectionRef.current &&
        connectionRef.current.state !== HubConnectionState.Disconnected)
    ) {
      return;
    }

    isConnectingRef.current = true;
    let connection: HubConnection | null = null;
    let isCancelled = false;

    const createConnection = async () => {
      try {
        const authToken = (await getSensitiveData("authToken")) || "";
        if (!authToken || isCancelled) {
          isConnectingRef.current = false;
          return;
        }

        // connection = new HubConnectionBuilder()
        //   .withUrl(apiVariables.CHAT_HUB_URL, {
        //     accessTokenFactory: () => authToken,
        //     skipNegotiation: false,
        //     withCredentials: true,
        //   })
        //   .withAutomaticReconnect()
        //   .configureLogging(LogLevel.Information)
        //   .build();

        connection = new HubConnectionBuilder()
          .withUrl(apiVariables.CHAT_HUB_URL, {
            accessTokenFactory: () => authToken,
            skipNegotiation: false,
            withCredentials: true,
          })
          .withAutomaticReconnect()
          .configureLogging(LogLevel.Information)
          .build();

        connection.onclose(() => {
          if (!isCancelled) {
            setIsConnected(false);
          }
        });

        connection.onreconnecting(() => {
          if (!isCancelled) {
            setIsConnected(false);
          }
        });

        connection.onreconnected(() => {
          const activeConnection = connection;
          if (isCancelled || !activeConnection) {
            return;
          }

          setIsConnected(true);
          activeConnection.invoke("JoinUserGroup", currentUserId).catch(() => {});
          if (conversations.length > 0) {
            conversations.forEach((conv) => {
              activeConnection.invoke("JoinConversation", conv.conversationId).catch(() => {});
            });
          }
        });

        connection.on("ReceiveMessage", (msg: any) => {
          if (!msg?.messageId) {
            return;
          }

          if (!msg?.conversationId) {
            return;
          }

          const messageKey = `${msg.conversationId}-${msg.messageId}`;  
          if (processedMessagesRef.current.has(messageKey)) {
            return; 
          }
          processedMessagesRef.current.add(messageKey);
          const isSent = msg.senderId?.toLowerCase() === currentUserId.toLowerCase();

          setConversations((prev) => {
            if (prev.length === 0) {
              return prev;
            }

            let updatedConversation: Conversation | null = null;
            let matchFound = false;
            
            const updated = prev.map((conv) => {
              if (conv.conversationId === msg.conversationId) {
                matchFound = true;
                
                updatedConversation = {
                  ...conv,
                  lastMessage: msg.messageContent || "",
                  time: formatTime(msg.sentAt || new Date().toISOString()),
                  unread: isSent ? conv.unread : conv.unread + 1,
                };
                
                return updatedConversation;
              }
              
              return conv;
            });

            if (!matchFound) {
              const otherUserId = isSent ? msg.receiverId : msg.senderId;
              const otherUserName = isSent
                ? msg.receiverName || "Unknown"
                : msg.senderName || "Unknown";

              if (!otherUserId) {
                return updated;
              }

              const newConversation: Conversation = {
                conversationId: msg.conversationId,
                receiverId: otherUserId,
                name: otherUserName,
                lastMessage: msg.messageContent || "",
                time: formatTime(msg.sentAt || new Date().toISOString()),
                unread: isSent ? 0 : 1,
                isOnline: false,
                profileImage: msg.senderProfileImageUrl || msg.profileImageUrl,
                photoId: msg.senderPhotoId || msg.photoId,
              };

              return [newConversation, ...updated];
            }

            if (updatedConversation) {
              const filtered = updated.filter(c => c.conversationId !== (updatedConversation as Conversation).conversationId);
              return [updatedConversation as Conversation, ...filtered];
            }

            return updated;
          });
        });

        connection.on("UserOnline", (payload: any) => {
          const targetUserId = payload?.UserId || payload?.userId || payload;
          setConversations((prev) =>
            prev.map((conv) =>
              conv.receiverId.toLowerCase() === targetUserId.toLowerCase()
                ? { ...conv, isOnline: true }
                : conv
            )
          );
        });

        connection.on("UserOffline", (payload: any) => {
          const targetUserId = payload?.UserId || payload?.userId || payload;
          setConversations((prev) =>
            prev.map((conv) =>
              conv.receiverId.toLowerCase() === targetUserId.toLowerCase()
                ? { ...conv, isOnline: false }
                : conv
            )
          );
        });

        await connection.start();

        if (isCancelled) {
          await connection.stop();
          return;
        }

        connectionRef.current = connection;
        setIsConnected(true);
        await connection.invoke("JoinUserGroup", currentUserId);
        isConnectingRef.current = false;

      } catch (err: any) {
        if (!isCancelled) {
          setIsConnected(false);
        }
        isConnectingRef.current = false;
      }
    };

    createConnection();

    return () => {
      isCancelled = true;
      const conn = connectionRef.current || connection;
      connectionRef.current = null;
      isConnectingRef.current = false;
      conn?.stop().catch(() => {});
    };
  }, [currentUserId]); 
  useEffect(() => {
    const conn = connectionRef.current;
    
    if (!conn || !isConnected || conversations.length === 0) {
      return;
    }
    
    const joinedConversations: number[] = [];
    
    conversations.forEach((conv) => {
      conn.invoke("JoinConversation", conv.conversationId)
        .then(() => {
          joinedConversations.push(conv.conversationId);
        })
        .catch(() => {});
    });

    return () => {
      joinedConversations.forEach((convId) => {
        conn.invoke("LeaveConversation", convId).catch(() => {
        });
      });
    };
  }, [conversations, isConnected]);



  const filteredConversations = useMemo(() => conversations.filter((c) => {
    const q = searchQuery.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.lastMessage.toLowerCase().includes(q)
    );
  }), [conversations, searchQuery]);

  return (
   <div
      className="h-screen md:h-auto flex flex-col px-4 pb-8"
      id="messages-container"
      style={{ backgroundColor: theme.background, color: theme.text }}
    >
      <div className="mb-6">
        <div className="relative p-[2px] bg-gradient-to-r from-brand-primary to-brand-gray rounded-lg">
          <div className="relative rounded-md" style={{ backgroundColor: theme.surface }}>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Message, People"
              className="w-full py-3 px-4 pr-12 text-[14px] bg-transparent outline-none"
              style={{ color: theme.text }}
            />
            <Search
              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5"
              style={{ color: theme.textSecondary }}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
         {loading ? (
            <div className="text-center py-8">Loading {labels.messages}</div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              {/* <p className="text-[#757575] text-[16px] mb-3">
                No Messages
              </p> */}
              <button
                onClick={() => navigate("/matches")}
               className="text-[16px] mb-3"
               style={{ color: theme.textSecondary }}
              >
                 No Messages.
              </button>
            </div>
          ) : (
          filteredConversations.map((c, index) => (
            <div key={c.conversationId}>
              <div
                onClick={() => {
                  onOpenChat(c.receiverId, c.name, c.photoId);
                }}
                className="rounded-lg p-2 my-1 flex items-center gap-3 cursor-pointer transition-colors"
                style={{ backgroundColor: theme.surface, color: theme.text }}
              >
                <PhotoImage
                  photoId={c.photoId}
                  alt={c.name}
                  className="w-14 h-14 rounded-lg object-cover"
                  fallback={
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-b from-brand-primary to-brand-gray flex items-center justify-center text-white text-[18px] font-[600]">
                      {getInitials(c.name)}
                    </div>
                  }
                />
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-[16px] truncate">{c.name}</h3>
                  <p className="text-[14px] truncate" style={{ color: theme.textSecondary }}>{c.lastMessage}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-[12px]" style={{ color: theme.textSecondary }}>{c.time}</span>
                  {c.unread > 0 && (
                    <div className="w-6 h-6 bg-brand-primary text-white rounded-full flex items-center justify-center text-[12px] font-medium flex-shrink-0">
                      {c.unread > 99 ? '99+' : c.unread}
                    </div>
                  )}
                </div>
              </div>
              {index < filteredConversations.length - 1 && (
                <div className="h-[1px] mx-4" style={{ backgroundColor: theme.border }} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function formatTime(dateString: string) {
  if (!dateString) return "";

  const normalized =
    /Z$|[+-]\d{2}:\d{2}$/.test(dateString)
      ? dateString
      : dateString + "Z";

  const date = new Date(normalized);
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);

  if (date >= startOfToday) {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  if (date >= startOfYesterday) {
    return "Yesterday";
  }

  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${yyyy}/${mm}/${dd}`;
}
